import useIsContainerBeyondBreakpoint from '@/hooks/useIsContainerBeyondBreakpoint';
import {
  Alert,
  Button,
  OverlayPortalContainerProvider,
  Sidebar,
  Tabs,
  Text,
  Tooltip,
} from '@payaca/ambrosia';
import UntitledIcon from '@payaca/untitled-icons';
import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  type InspectorDestination,
  type InspectorFocusRequest,
} from './InspectorNavigation';
import { InspectorFooterContext } from './inspectorFooter';

export type InspectorTask = 'roof' | 'equipment' | 'electrical' | 'performance';

type DesignerWorkspaceProps = {
  actions: ReactNode;
  map: ReactNode;
  roof: ReactNode;
  equipment: ReactNode;
  /** Omitted when the design has no roof model to allocate panels against. */
  electrical?: ReactNode;
  performance: ReactNode;
  summary: ReactNode;
  mapToolRail?: ReactNode;
  mapToolbar?: ReactNode;
  /** Cards stacked over the top right of the map, without moving it. */
  notices?: ReactNode;
  contextKey: string;
  hasSelection: boolean;
  hasActiveTool?: boolean;
  isBusy: boolean;
  missingInverter?: boolean;
  /** Where inverter units are chosen: Electrical once allocation owns them. */
  inverterTask?: 'equipment' | 'electrical';
  focusRequest?: InspectorFocusRequest | null;
  onTaskChange?: () => void;
};

const TASK_LABELS: Record<InspectorTask, string> = {
  roof: 'Roof',
  equipment: 'Equipment',
  electrical: 'Electrical',
  performance: 'Performance',
};

const DESTINATION_TASK: Record<InspectorDestination, InspectorTask> = {
  layout: 'roof',
  mounting: 'roof',
  alignment: 'roof',
  reference: 'roof',
  spacing: 'roof',
  inverter: 'equipment',
  equipment: 'equipment',
  electrical: 'electrical',
  tariff: 'performance',
};

const Inspector: FC<{
  roof: ReactNode;
  equipment: ReactNode;
  electrical?: ReactNode;
  performance: ReactNode;
  missingInverter: boolean;
  inverterTask: 'equipment' | 'electrical';
  summary: ReactNode;
  selectedTask: InspectorTask;
  onTaskChange: (task: InspectorTask) => void;
  scrollPositions: Record<InspectorTask, number>;
  /** Sits at the right end of the tab strip (the desktop collapse control). */
  tabSuffix?: ReactNode;
}> = (props) => {
  const {
    roof,
    equipment,
    electrical,
    performance,
    missingInverter,
    inverterTask,
    summary,
    selectedTask,
    onTaskChange,
    scrollPositions,
    tabSuffix,
  } = props;
  const tasks: InspectorTask[] = [
    'roof',
    'equipment',
    ...(electrical ? (['electrical'] as const) : []),
    'performance',
  ];
  const [footer, setFooter] = useState<HTMLDivElement | null>(null);
  // A task that disappears (no roof model) falls back to Roof.
  const selectedIndex = Math.max(0, tasks.indexOf(selectedTask));
  const activeTask = tasks[selectedIndex];
  const scroller = useRef<HTMLElement | null>(null);
  const capturePanel = useCallback((node: HTMLDivElement | null) => {
    if (!node) return;
    const container = node.closest('[role="tabpanel"]')?.parentElement;
    if (container) scroller.current = container;
  }, []);
  useLayoutEffect(() => {
    if (scroller.current)
      scroller.current.scrollTop = scrollPositions[activeTask] ?? 0;
  }, [activeTask, scrollPositions]);
  const content: Record<InspectorTask, ReactNode> = {
    roof,
    equipment,
    electrical,
    performance: (
      <>
        {missingInverter && (
          <div className="mb-3">
            <Alert colour="yellow" iconName="alert-triangle.3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Text>Inverter not included</Text>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => onTaskChange(inverterTask)}
                >
                  {inverterTask === 'electrical'
                    ? 'Open Electrical'
                    : 'Open Equipment'}
                </Button>
              </div>
            </Alert>
          </div>
        )}
        {performance}
      </>
    ),
  };
  return (
    <div
      className="flex min-h-0 flex-1 flex-col"
      onScrollCapture={(event) => {
        if (event.target === scroller.current)
          scrollPositions[activeTask] = scroller.current.scrollTop;
      }}
    >
      <InspectorFooterContext.Provider value={footer}>
        <Tabs
          selectedIndex={selectedIndex}
          onChange={(index) => onTaskChange(tasks[index])}
          fullWidth
          suffix={tabSuffix}
          className="shrink-0 px-4 pt-2 [&>[role=tablist]]:min-w-0 [&>[role=tablist]]:flex-1"
          classNameTabPanels="mt-0 min-h-0 flex-1 overflow-y-auto overscroll-contain p-4"
        >
          {tasks.map((task) => (
            <Tabs.Panel
              key={task}
              label={TASK_LABELS[task]}
              render={() => <div ref={capturePanel}>{content[task]}</div>}
            />
          ))}
        </Tabs>
      </InspectorFooterContext.Provider>
      <div
        ref={setFooter}
        className="max-h-80 shrink-0 overflow-y-auto overscroll-contain border-t border-gray-200 px-4 py-3 empty:hidden"
      />
      <div className="shrink-0 border-t border-gray-200 bg-gray-50 px-4 py-3">
        {summary}
      </div>
    </div>
  );
};

const DesignerWorkspace: FC<DesignerWorkspaceProps> = (props) => {
  const {
    actions,
    map,
    roof,
    equipment,
    electrical,
    performance,
    summary,
    mapToolRail,
    mapToolbar,
    notices,
    isBusy,
    hasActiveTool = false,
    missingInverter = false,
    inverterTask = 'equipment',
    focusRequest,
    onTaskChange,
  } = props;
  const isDesktop = useIsContainerBeyondBreakpoint('md');
  const [desktopOpen, setDesktopOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<InspectorTask>('roof');
  const scrollPositions = useRef<Record<InspectorTask, number>>({
    roof: 0,
    equipment: 0,
    electrical: 0,
    performance: 0,
  });
  const changeTask = (task: InspectorTask) => {
    setSelectedTask(task);
    onTaskChange?.();
  };

  // Tool instructions (draw, obstacle, move, trace, pin) live in the Roof tab.
  useEffect(() => {
    if (hasActiveTool) setSelectedTask('roof');
  }, [hasActiveTool]);

  useEffect(() => {
    if (!focusRequest) return;
    setSelectedTask(DESTINATION_TASK[focusRequest.destination]);
    setDesktopOpen(true);
    if (!isDesktop) setMobileOpen(true);
    // These destinations have no field that reports the focus as handled.
    if (
      ['layout', 'spacing', 'tariff', 'electrical'].includes(
        focusRequest.destination
      )
    )
      onTaskChange?.();
  }, [focusRequest, isDesktop, onTaskChange]);

  useEffect(() => {
    setMobileOpen(false);
  }, [isDesktop]);

  const renderInspector = (tabSuffix?: ReactNode) => (
    <Inspector
      roof={roof}
      equipment={equipment}
      electrical={electrical}
      performance={performance}
      missingInverter={missingInverter}
      inverterTask={inverterTask}
      summary={summary}
      selectedTask={selectedTask}
      onTaskChange={changeTask}
      scrollPositions={scrollPositions.current}
      tabSuffix={tabSuffix}
    />
  );

  const toolbar = mapToolbar && (
    <div
      className="flex min-w-0 items-start gap-2 [&>*]:pointer-events-auto"
      inert={isBusy}
    >
      {mapToolbar}
    </div>
  );

  return (
    <div className="flex h-full min-h-0 flex-col bg-white" aria-busy={isBusy}>
      <header className="shrink-0 border-b border-gray-200 px-4 py-3">
        {actions}
      </header>
      <div className="relative flex min-h-0 flex-1">
        <div className="relative min-w-0 flex-1">
          <div
            className="relative h-full"
            data-testid="solar-design-map"
            inert={isBusy}
          >
            {map}
          </div>
          <div className="pointer-events-none absolute left-4 right-4 top-4 flex flex-wrap items-start gap-2">
            <div
              className={
                isDesktop ? 'contents' : 'flex min-w-0 items-start gap-2'
              }
            >
              {mapToolRail && (
                <div
                  className="shrink-0 [&>*]:pointer-events-auto"
                  inert={isBusy}
                >
                  {mapToolRail}
                </div>
              )}
              {!isDesktop && toolbar}
            </div>
            <div
              className={
                isDesktop
                  ? 'flex min-w-0 flex-1 flex-wrap items-start gap-2'
                  : 'contents'
              }
            >
              {isDesktop && toolbar}
              <div
                className={`flex flex-col items-end gap-2 ${isDesktop ? 'min-w-xs flex-1' : 'ml-auto'} ${notices && !isDesktop ? 'w-full' : ''}`}
              >
                {(!isDesktop || !desktopOpen) && (
                  <div className="pointer-events-auto" inert={isBusy}>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() =>
                        isDesktop ? setDesktopOpen(true) : setMobileOpen(true)
                      }
                    >
                      <UntitledIcon name="sliders-01.3" className="size-4" />
                      Design controls
                    </Button>
                  </div>
                )}
                {notices && (
                  <div
                    className={`flex flex-col gap-2 ${isDesktop ? 'items-end' : 'w-full'}`}
                  >
                    {notices}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {isDesktop && desktopOpen && (
          <aside
            inert={isBusy}
            aria-label="Design controls"
            className="flex w-lg shrink-0 flex-col border-l border-gray-200"
          >
            {renderInspector(
              <Tooltip tooltipContent="Hide design controls">
                <Button
                  variant="quaternary"
                  size="sm"
                  className="shrink-0"
                  aria-label="Hide design controls"
                  onClick={() => setDesktopOpen(false)}
                >
                  <UntitledIcon name="chevron-right" className="size-4" />
                </Button>
              </Tooltip>
            )}
          </aside>
        )}
      </div>
      <OverlayPortalContainerProvider value={null}>
        <Sidebar
          title="Solar design"
          isOpen={!isDesktop && mobileOpen}
          onClose={() => setMobileOpen(false)}
          showCloseControl={false}
          modal
        >
          <div className="flex min-h-0 flex-1 flex-col" inert={isBusy}>
            {renderInspector()}
            <Sidebar.Footer>
              <Button
                variant="secondary"
                className="w-full"
                onClick={() => setMobileOpen(false)}
              >
                <UntitledIcon name="arrow-left" className="size-4" />
                Back to roof
              </Button>
            </Sidebar.Footer>
          </div>
        </Sidebar>
      </OverlayPortalContainerProvider>
    </div>
  );
};

export default DesignerWorkspace;
